import { ProposalBlockFormData } from '../../ProposalForm';

// A section and the blocks that follow it (its sub-sections). Blocks before the first section form
// a leading group with no section of its own.
interface BlockGroup {
  isSection: boolean;
  items: ProposalBlockFormData[];
}

function toGroups(blocks: ProposalBlockFormData[]): BlockGroup[] {
  const groups: BlockGroup[] = [];
  blocks.forEach(block => {
    if (block.isSection || groups.length === 0) {
      groups.push({ isSection: !!block.isSection, items: [block] });
    } else {
      groups[groups.length - 1].items.push(block);
    }
  });
  return groups;
}

function renumber(blocks: ProposalBlockFormData[]): ProposalBlockFormData[] {
  return blocks.map((block, index) => ({ ...block, menuOrder: index }));
}

export interface BlockMoveResult {
  blocks: ProposalBlockFormData[];
  // Where the dragged block ended up, for scrolling the page to it.
  index: number;
}

/**
 * Reorders the builder's flat block list after a drag.
 *
 * Dragging a **section** moves the whole group (the section plus its sub-sections) and snaps it to
 * a group boundary, so a section can never be dropped into the middle of another section's blocks —
 * that is what used to steal/absorb sub-sections. Dragging a plain block moves just that block
 * (landing under whichever section precedes its new position).
 *
 * Indexes are positions in the flat list, exactly as react-sortable-hoc reports them.
 */
export function moveBlock(blocks: ProposalBlockFormData[], oldIndex: number, newIndex: number): BlockMoveResult {
  const dragged = blocks[oldIndex];
  if (!dragged || oldIndex === newIndex) return { blocks, index: newIndex };

  // Plain block: a simple move — its new parent is just whichever section now precedes it.
  if (!dragged.isSection) {
    const next = [...blocks];
    next.splice(newIndex, 0, ...next.splice(oldIndex, 1));
    return { blocks: renumber(next), index: newIndex };
  }

  const groups = toGroups(blocks);

  // Locate the dragged section's group, and the group the drop position falls into.
  let cursor = 0;
  let sourceGroup = -1;
  let targetGroup = -1;
  groups.forEach((group, groupIndex) => {
    const start = cursor;
    const end = cursor + group.items.length - 1;
    if (oldIndex >= start && oldIndex <= end) sourceGroup = groupIndex;
    if (newIndex >= start && newIndex <= end) targetGroup = groupIndex;
    cursor = end + 1;
  });
  // Dropped past the end of the list.
  if (targetGroup === -1) targetGroup = groups.length - 1;
  // Dropped on itself or on one of its own sub-sections.
  if (sourceGroup === -1 || sourceGroup === targetGroup) return { blocks, index: oldIndex };

  const moving = groups[sourceGroup];
  const rest = groups.filter((_, groupIndex) => groupIndex !== sourceGroup);
  // The same index either way: dragging down, removing the source shifts the target left by one, so
  // this lands AFTER the target group; dragging up, it lands BEFORE it.
  let insertAt = targetGroup;

  // A section cannot sit above the leading group of section-less blocks — it would swallow them.
  if (insertAt === 0 && rest.length && !rest[0].isSection) insertAt = 1;

  rest.splice(insertAt, 0, moving);

  const next = rest.reduce<ProposalBlockFormData[]>((acc, group) => acc.concat(group.items), []);
  return { blocks: renumber(next), index: next.findIndex(block => block.id === dragged.id) };
}
